import React, { useState, useRef } from 'react';
import { Text, Button, Input, Label, Spinner, makeStyles } from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { useAuth } from '../context/AuthContext';
import { authClient, BEARER_TOKEN_KEY } from '../utils/auth-client';
import { apiUrl } from '../utils/api';
import { useNavigate } from 'react-router';
import KmvsWordmark from '../assets/kmvs-wordmark.png';

type Method = 'microsoft' | 'password' | 'passkey';
type Step = 'email' | 'method';

const useStyles = makeStyles({
  root: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    margin: 0,
    padding: 0,
    position: 'absolute',
    top: 0,
    left: 0,
    fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      height: 'auto',
      minHeight: '100vh',
    },
  },
  leftSide: {
    flex: 1,
    backgroundColor: '#36203c',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '@media (max-width: 768px)': {
      flex: 'none',
      height: '35vh',
      minHeight: '220px',
      justifyContent: 'center',
      paddingBottom: '20px',
    },
  },
  logo: {
    height: '150px',
    width: 'auto',
    maxWidth: '78%',
    objectFit: 'contain',
    '@media (max-width: 768px)': {
      height: '96px',
    },
  },
  rightSide: {
    flex: 1,
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (max-width: 768px)': {
      flex: 1,
      justifyContent: 'flex-start',
      paddingTop: '40px',
      paddingBottom: '40px',
    },
  },
  formContainer: {
    width: '400px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '@media (max-width: 768px)': {
      width: '85%',
      maxWidth: '400px',
    },
  },
});

const METHOD_LABELS: Record<Method, string> = {
  microsoft: 'Einloggen über Microsoft',
  passkey: 'Mit Passkey anmelden',
  password: 'Mit Passwort anmelden',
};

export const LoginPage: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [methods, setMethods] = useState<Method[]>([]);
  const [showOther, setShowOther] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cache: don't re-fire Discovery for the same email on every blur/focus.
  const lastQueriedEmailRef = useRef<string>('');

  // Pick the single primary method shown by default. Microsoft first, then
  // Passkey, then Password. Everything else lives behind the "Auf andere
  // Weise anmelden"-Toggle.
  const primaryMethod: Method = methods.includes('microsoft')
    ? 'microsoft'
    : methods.includes('passkey')
      ? 'passkey'
      : 'password';

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Toten Bearer-Token aufräumen. Beim Microsoft-SSO kommt der `set-auth-token`
  // nie ins localStorage (Full-Page-Redirect statt XHR), der Login lebt also vom
  // Session-Cookie. Liegt aber noch ein alter, korrekt signierter Token aus einer
  // abgelaufenen Session in localStorage, injiziert ihn der better-auth bearer-Hook
  // bei jedem Request als Session-Cookie (er prüft nur die Signatur, nicht die
  // Gültigkeit) und überschattet damit das frische, gültige Cookie → getSession
  // liefert null → Login-Loop. Wer auf der Login-Seite landet und nicht (mehr)
  // eingeloggt ist, hat per Definition keinen brauchbaren Token → entfernen, damit
  // der nächste Anlauf sauber übers Cookie läuft.
  React.useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      localStorage.removeItem(BEARER_TOKEN_KEY);
    }
  }, [isAuthLoading, isAuthenticated]);

  const resetToEmail = () => {
    setStep('email');
    setPassword('');
    setMethods([]);
    setShowOther(false);
    setError(null);
    lastQueriedEmailRef.current = '';
  };

  const runDiscovery = async (rawEmail: string): Promise<boolean> => {
    const normalized = rawEmail.trim().toLowerCase();
    if (!normalized || !normalized.includes('@')) return false;
    if (normalized === lastQueriedEmailRef.current && step === 'method') return true;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(apiUrl('auth-discovery/methods'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalized }),
      });
      if (!res.ok) throw new Error(`Discovery fehlgeschlagen (${res.status})`);
      const json = (await res.json()) as { methods?: Method[] };
      const available = Array.isArray(json.methods) && json.methods.length > 0
        ? json.methods
        : (['microsoft'] as Method[]);
      setMethods(available);
      // If there's no Microsoft primary, expand the others immediately —
      // otherwise we'd render nothing on screen.
      setShowOther(!available.includes('microsoft'));
      setStep('method');
      lastQueriedEmailRef.current = normalized;
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message?.trim() : '';
      setError(message || 'Anmeldeoptionen konnten nicht ermittelt werden.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!normalized || !normalized.includes('@')) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    await runDiscovery(normalized);
  };

  // Tab/blur trigger — same Discovery call as the submit handler. Fires only
  // when the value parses as an email and hasn't been queried already, so
  // re-focusing the field doesn't re-fetch.
  const handleEmailBlur = () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized.includes('@') || normalized.length < 5) return;
    if (normalized === lastQueriedEmailRef.current) return;
    void runDiscovery(normalized);
  };

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    setError(null);
    // Vor dem Redirect den (potenziell abgelaufenen) Token verwerfen: der
    // Microsoft-Flow kann ihn danach nicht neu setzen und ein toter Token würde
    // das frische Session-Cookie überschatten (s. useEffect oben).
    localStorage.removeItem(BEARER_TOKEN_KEY);
    try {
      const result = await authClient.signIn.social({
        provider: 'microsoft',
        // login_hint sagt Microsoft, welcher Account gemeint ist — kombiniert
        // mit prompt=select_account auf dem Server zeigt MSFT entweder direkt
        // den passenden Account-Picker-Eintrag oder lässt den User wechseln.
        loginHint: email.trim().toLowerCase(),
      });
      if (result?.error) {
        const message = result.error.message?.trim();
        setError(message || 'Microsoft-Anmeldung konnte nicht gestartet werden.');
        setIsLoading(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message?.trim() : '';
      setError(message || 'Microsoft-Anmeldung konnte nicht gestartet werden.');
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const result = await authClient.signIn.email({ email: email.trim().toLowerCase(), password });
      // If 2FA is enabled, the twoFactorClient hook redirects to /two-factor.
      if ((result as any)?.data?.twoFactorRedirect) return;
      if (result?.error || !result?.data?.user) {
        const message = result?.error?.message?.trim();
        setError(
          message || 'Anmeldung fehlgeschlagen. Bitte E-Mail-Adresse und Passwort prüfen.',
        );
        return;
      }
      window.location.href = '/';
    } catch (err) {
      const message = err instanceof Error ? err.message?.trim() : '';
      setError(message || 'Unerwarteter Fehler bei der Anmeldung.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await (authClient as any).signIn.passkey({
        email: email.trim().toLowerCase(),
      });
      if ((result as any)?.data?.twoFactorRedirect) return;
      if (result?.error) {
        const message = result.error.message?.trim();
        setError(message || 'Passkey-Anmeldung fehlgeschlagen.');
        return;
      }
      window.location.href = '/';
    } catch (err) {
      const message = err instanceof Error ? err.message?.trim() : '';
      setError(message || 'Passkey-Anmeldung wurde abgebrochen.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.leftSide}>
        <img src={KmvsWordmark} alt="kmvs · Courté J. & Co. KG" className={styles.logo} />
      </div>

      <div className={styles.rightSide}>
        <div className={styles.formContainer}>
          <Text size={800} weight="semibold" style={{ color: '#242424', marginBottom: '8px', textAlign: 'center' }}>
            Willkommen zurück
          </Text>
          <Text size={300} style={{ color: '#605e5c', marginBottom: '40px', textAlign: 'center' }}>
            {step === 'email'
              ? 'Bitte gib deine E-Mail-Adresse ein.'
              : 'Wähle eine Anmeldeoption.'}
          </Text>

          {error && (
            <div style={{ width: '100%', padding: '12px', backgroundColor: '#fde7e9', color: '#a80000', borderRadius: '4px', marginBottom: '24px', textAlign: 'center' }}>
              <Text>{error}</Text>
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Label htmlFor="emailInput" size="small">E-Mail-Adresse</Label>
                <Input
                  id="emailInput"
                  type="email"
                  value={email}
                  onChange={(_e, data) => setEmail(data.value)}
                  onBlur={handleEmailBlur}
                  required
                  disabled={isLoading}
                  autoFocus
                  style={{ height: '40px' }}
                />
              </div>
              <Button
                type="submit"
                appearance="primary"
                disabled={isLoading || !email.includes('@')}
                style={{
                  width: '100%',
                  height: '48px',
                  backgroundColor: '#36203c',
                  color: '#ffffff',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginTop: '8px',
                }}
              >
                {isLoading ? <Spinner size="tiny" /> : 'Weiter'}
              </Button>
            </form>
          )}

          {step === 'method' && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Button
                  appearance="subtle"
                  icon={<ArrowLeftRegular />}
                  onClick={resetToEmail}
                  disabled={isLoading}
                  size="small"
                >
                  zurück
                </Button>
                <Text size={200} style={{ color: '#605e5c' }}>{email}</Text>
              </div>

              {primaryMethod === 'microsoft' && (
                <Button
                  appearance="primary"
                  onClick={handleMicrosoftLogin}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    height: '48px',
                    backgroundColor: '#36203c',
                    color: '#ffffff',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  {METHOD_LABELS.microsoft}
                </Button>
              )}

              {primaryMethod === 'passkey' && !showOther && (
                <Button
                  appearance="primary"
                  onClick={handlePasskeyLogin}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    height: '48px',
                    backgroundColor: '#36203c',
                    color: '#ffffff',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  {METHOD_LABELS.passkey}
                </Button>
              )}

              {/* "Auf andere Weise anmelden" — only relevant when primary
                  is microsoft or passkey. If primary is password, the form
                  below is already the only option. */}
              {primaryMethod !== 'password' && !showOther && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
                  <div style={{ flex: 1, height: 1, backgroundColor: '#e1dfdd' }} />
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => setShowOther(true)}
                    disabled={isLoading}
                  >
                    Auf andere Weise anmelden
                  </Button>
                  <div style={{ flex: 1, height: 1, backgroundColor: '#e1dfdd' }} />
                </div>
              )}

              {(showOther || primaryMethod === 'password') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Don't double-render the primary passkey button when it's
                      already the headline option. */}
                  {methods.includes('passkey') && primaryMethod !== 'passkey' && (
                    <Button
                      appearance="secondary"
                      onClick={handlePasskeyLogin}
                      disabled={isLoading}
                      style={{ width: '100%', height: '44px' }}
                    >
                      {METHOD_LABELS.passkey}
                    </Button>
                  )}

                  {/* Password is always available as a fallback — a wrong attempt
                      just gets a clean 401. */}
                  <form onSubmit={handlePasswordLogin} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Label htmlFor="passwordInput" size="small">Passwort</Label>
                      <Input
                        id="passwordInput"
                        type="password"
                        value={password}
                        onChange={(_e, data) => setPassword(data.value)}
                        required
                        disabled={isLoading}
                        autoFocus
                        style={{ height: '40px' }}
                      />
                    </div>
                    <Button
                      type="submit"
                      appearance="secondary"
                      disabled={isLoading || !password}
                      style={{ height: '44px' }}
                    >
                      {isLoading ? <Spinner size="tiny" /> : METHOD_LABELS.password}
                    </Button>
                  </form>
                </div>
              )}

              {isLoading && !showOther && primaryMethod !== 'password' && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                  <Spinner size="tiny" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
